import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Play, Star, Menu, X, ChevronLeft, ChevronRight,
  Brain, Users, RefreshCw, Globe, Video, BarChart3, Plug, ShieldCheck,
  Sun, Moon
} from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';

/* ─── Navbar ─── */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggle: toggleTheme } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${scrolled ? 'bg-[#09090b]/80 backdrop-blur-xl border-b border-white/[0.04]' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-10">
            <Link to="/" className="flex items-center gap-2.5 group">
              <img src="https://img.icons8.com/parakeet-line/48/tear-off-calendar.png" alt="ScheduleX" className="w-8 h-8 transition-transform duration-300 group-hover:rotate-12" />
              <span className="text-white font-bold text-lg tracking-tight">ScheduleX</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              {['Features','Demo','Pricing'].map(item => (
                <a key={item} href={`#${item.toLowerCase()}`} className="text-sm text-zinc-500 hover:text-white transition-colors relative group">
                  {item}
                  <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-white transition-all duration-300 group-hover:w-full" />
                </a>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden sm:block text-sm text-zinc-400 hover:text-white px-4 py-2">Log in</Link>
            <button onClick={toggleTheme} className="text-sm text-zinc-400 hover:text-white px-3 py-2" aria-label="Toggle theme">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <Link to="/register" className="text-sm font-semibold px-5 py-2.5 rounded-xl bg-white text-black hover:bg-zinc-200 transition-all">Start for free</Link>
            <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-zinc-400 hover:text-white p-2">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-[99] bg-[#09090b]/97 backdrop-blur-xl flex flex-col items-center justify-center gap-6 md:hidden">
          <button onClick={() => setMobileOpen(false)} className="absolute top-5 right-5 text-zinc-500 hover:text-white p-2"><X className="w-6 h-6" /></button>
          {['features','demo','pricing'].map(item => (
            <a key={item} href={`#${item}`} onClick={() => setMobileOpen(false)} className="text-xl text-zinc-400 hover:text-white capitalize">{item}</a>
          ))}
          <button onClick={() => { toggleTheme(); setMobileOpen(false); }} className="text-xl text-zinc-400 hover:text-white flex items-center gap-2">
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            <span className="capitalize">{theme === 'dark' ? 'Light' : 'Dark'} mode</span>
          </button>
          <Link to="/login" onClick={() => setMobileOpen(false)} className="text-xl text-zinc-400 hover:text-white">Log in</Link>
          <Link to="/register" onClick={() => setMobileOpen(false)} className="text-xl text-zinc-400 hover:text-white">Start for free</Link>
        </div>
      )}
    </>
  );
}

/* ─── Hero ─── */
function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
        <div className="rounded-full border border-white/[0.04] animate-[spin_40s_linear_infinite]" style={{ width: 700, height: 700 }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/[0.04] animate-[spin_55s_linear_infinite_reverse]" style={{ width: 500, height: 500 }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/[0.04] animate-[spin_70s_linear_infinite]" style={{ width: 300, height: 300 }} />
      </div>
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-white/[0.03] rounded-full blur-[120px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-24 pb-16">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-white/[0.08] bg-white/[0.03] mb-8">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-white/30 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              <span className="text-xs font-medium text-zinc-500">10,000+ teams already onboard</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-[76px] font-extrabold tracking-tight leading-[1.02] mb-7">
              Scheduling<br />
              <span className="text-zinc-500">made</span> <span className="italic font-light text-zinc-400">ridiculously</span><br />
              simple.
            </h1>

            <p className="text-lg text-zinc-500 max-w-lg mb-10 leading-relaxed font-light">
              Stop playing email ping-pong. ScheduleX finds perfect times, syncs every calendar, and gets out of your way.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-14">
              <Link to="/register" className="px-8 py-4 rounded-2xl text-base font-semibold bg-white text-black flex items-center gap-2.5 justify-center w-full sm:w-auto hover:-translate-y-0.5 transition-all duration-300">
                Start for free <ArrowRight className="w-[17px] h-[17px]" />
              </Link>
              <button onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })} className="px-8 py-4 rounded-2xl text-base font-medium border border-white/[0.1] text-zinc-400 flex items-center gap-2.5 justify-center w-full sm:w-auto hover:bg-white/5 hover:border-white/25 transition-all">
                <Play className="w-[17px] h-[17px]" /> See it in action
              </button>
            </div>

            <div className="flex items-end gap-10">
              {[
                { val: '10K+', label: 'Users' },
                { val: '500K', label: 'Bookings' },
                { val: '99.9%', label: 'Uptime' },
                { val: '4.9', label: 'Rating', icon: <Star className="w-8 h-8 mb-1" /> },
              ].map((s) => (
                <div key={s.label} className={s.label === 'Rating' ? 'hidden sm:block' : ''}>
                  <div className="text-[clamp(48px,8vw,96px)] font-extrabold leading-none tracking-[-0.04em] bg-gradient-to-b from-[#fafafa] to-[#52525b] bg-clip-text text-transparent flex items-end gap-1">
                    {s.val}{s.icon}
                  </div>
                  <div className="text-xs text-zinc-600 mt-1.5 uppercase tracking-widest font-medium">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5" style={{ animation: 'scaleIn .9s .5s cubic-bezier(.16,1,.3,1) forwards, float 7s ease-in-out infinite', opacity: 0 }}>
            <div className="relative">
              <div className="absolute -inset-4 bg-white/[0.03] rounded-3xl blur-2xl" />
              <div className="relative bg-zinc-900/80 backdrop-blur-2xl border border-white/[0.08] rounded-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                  <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-zinc-700" /><div className="w-2.5 h-2.5 rounded-full bg-zinc-700" /><div className="w-2.5 h-2.5 rounded-full bg-zinc-700" /></div>
                  <div className="flex-1 text-center text-[11px] text-zinc-600 font-medium">schedulex.site</div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-white">June 2026</span>
                    <div className="flex gap-1">
                      <div className="w-6 h-6 rounded bg-white/[0.05] flex items-center justify-center"><ChevronLeft className="w-3 h-3 text-zinc-500" /></div>
                      <div className="w-6 h-6 rounded bg-white/[0.05] flex items-center justify-center"><ChevronRight className="w-3 h-3 text-zinc-500" /></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center mb-2">
                    {['M','T','W','T','F','S','S'].map((d,i) => (
                      <div key={i} className={`text-[10px] font-medium py-1 ${i>=5?'text-zinc-700':'text-zinc-600'}`}>{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center">
                    {[16,17,18,19,20,21,22].map((n,i) => (
                      <div key={n} className={`text-xs py-1.5 rounded-lg ${n===20?'text-white font-bold bg-white/10':i>=5?'text-zinc-700':'text-zinc-500'}`}>{n}</div>
                    ))}
                  </div>
                </div>
                <div className="px-4 pb-4 space-y-2">
                  {[
                    { t: 'Team Standup', time: '9:00 – 9:30 · 6 people', bar: 'bg-white', p: 4 },
                    { t: 'Design Review', time: '11:00 – 12:00 · 3 people', bar: 'bg-zinc-400', p: 3 },
                    { t: '1:1 with Sarah', time: '2:00 – 2:30', bar: 'bg-zinc-600', p: 0 },
                    { t: 'Sprint Planning', time: '4:00 – 5:00 · 8 people', bar: 'bg-zinc-600', p: 6 },
                  ].map((e,i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/[0.06]">
                      <div className={`w-1 h-10 ${e.bar} rounded-full`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-white truncate">{e.t}</div>
                        <div className="text-[11px] text-zinc-500">{e.time}</div>
                      </div>
                      {e.p > 0 && (
                        <div className="flex -space-x-2 shrink-0">
                          {Array.from({length:Math.min(e.p,2)}).map((_,j)=>(<div key={j} className={`w-5 h-5 rounded-full border border-zinc-900 ${j===0?'bg-zinc-700':'bg-zinc-600'}`} />))}
                          {e.p>2&&<div className="w-5 h-5 rounded-full bg-zinc-500 border border-zinc-900 text-[8px] flex items-center justify-center text-white font-bold">+{e.p-2}</div>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Marquee ─── */
function Marquee() {
  const brands = ['STRIPE','NOTION','LINEAR','VERCEL','FIGMA','SLACK','GITHUB','GITLAB'];
  return (
    <section className="py-8 border-y border-white/[0.04] overflow-hidden">
      <div className="flex" style={{ animation: 'marquee 30s linear infinite', width: 'max-content' }}>
        {[0,1].map(k => (
          <div key={k} className="flex items-center gap-12 px-6">
            {brands.flatMap(n => [<span key={n+k} className="text-3xl font-extrabold text-zinc-800 tracking-tight whitespace-nowrap">{n}</span>,<span key={`s${n}${k}`} className="text-zinc-700">&#10022;</span>])}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── Bento Card ─── */
function BentoCard({ icon, title, desc }) {
  return (
    <div className="p-6 flex flex-col justify-between rounded-[20px] bg-white/[0.02] border border-white/[0.06] transition-all duration-500 hover:border-white/[0.12] hover:-translate-y-[3px]">
      <div className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center">{icon}</div>
      <div>
        <h3 className="text-white font-semibold mb-1">{title}</h3>
        <p className="text-zinc-600 text-xs leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

/* ─── Features ─── */
function FeaturesSection() {
  return (
    <section id="features" className="py-28 px-6 relative">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/[0.015] rounded-full blur-[150px] pointer-events-none" />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-600 font-semibold mb-3">Features</p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Built for teams<br />who <span className="italic font-light text-zinc-500">actually</span> work.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4 auto-rows-[180px]">
          <div className="md:col-span-2 md:row-span-2 p-8 flex flex-col justify-between rounded-[20px] bg-white/[0.02] border border-white/[0.06] transition-all duration-500 hover:border-white/[0.12] hover:-translate-y-[3px]">
            <div>
              <div className="w-12 h-12 rounded-2xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center mb-5"><Brain className="w-6 h-6 text-white" /></div>
              <h3 className="text-2xl font-bold text-white mb-2">Smart Scheduling</h3>
              <p className="text-zinc-500 text-sm leading-relaxed max-w-md">AI learns when your team actually focuses, when they prefer meetings, and finds the perfect slot automatically.</p>
            </div>
            <div className="flex items-end gap-1.5 mt-4">
              {[4,8,12,6,16,10,20,14,8,5,11,3].map((h,i)=>(<div key={i} className="w-full rounded" style={{height:`${h*4}px`,backgroundColor:i===6?'rgba(255,255,255,0.15)':'rgba(255,255,255,0.04)',border:i===6?'1px solid rgba(255,255,255,0.1)':undefined}}/>))}
            </div>
          </div>
          <BentoCard icon={<Users className="w-5 h-5 text-white" />} title="Team Management" desc="Groups, roles, availability — all from one place." />
          <BentoCard icon={<RefreshCw className="w-5 h-5 text-white" />} title="Calendar Sync" desc="Google, Outlook, Apple — all bidirectional." />
          <div className="md:col-span-2 p-6 flex items-center gap-8 rounded-[20px] bg-white/[0.02] border border-white/[0.06] transition-all duration-500 hover:border-white/[0.12] hover:-translate-y-[3px]">
            <div className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center shrink-0"><Globe className="w-5 h-5 text-white" /></div>
            <div className="flex-1">
              <h3 className="text-white font-semibold mb-1">Time Zone Support</h3>
              <p className="text-zinc-600 text-xs leading-relaxed mb-3">Automatic detection. No math required.</p>
              <div className="flex gap-3 flex-wrap">
                {['UTC-8 9:00 AM','UTC+0 5:00 PM','UTC+9 2:00 AM+1'].map(tz=>(<span key={tz} className="px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.06] text-[11px] text-zinc-400 font-medium">{tz}</span>))}
              </div>
            </div>
          </div>
          <BentoCard icon={<Video className="w-5 h-5 text-white" />} title="Meeting Rooms" desc="Auto-assign based on capacity & gear." />
          <div className="md:col-span-2 p-6 rounded-[20px] bg-white/[0.02] border border-white/[0.06] transition-all duration-500 hover:border-white/[0.12] hover:-translate-y-[3px]">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center shrink-0"><BarChart3 className="w-5 h-5 text-white" /></div>
              <div>
                <h3 className="text-white font-semibold mb-1">Analytics Dashboard</h3>
                <p className="text-zinc-600 text-xs leading-relaxed">See where your team&apos;s time actually goes.</p>
              </div>
            </div>
            <div className="flex items-end gap-[3px] h-12">
              {[30,50,40,70,60,45,100,80,55,35,50,25,40,65,50,30,20,35,45,28].map((h,i)=>(<div key={i} className="flex-1 bg-white/[0.06] rounded-t" style={{height:`${h}%`}}/>))}
            </div>
          </div>
          <BentoCard icon={<Plug className="w-5 h-5 text-white" />} title="100+ Integrations" desc="Slack, Zoom, Teams, Notion, Jira…" />
          <div className="md:col-span-3 p-6 flex items-center gap-8 rounded-[20px] bg-white/[0.02] border border-white/[0.06] transition-all duration-500 hover:border-white/[0.12] hover:-translate-y-[3px]">
            <div className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center shrink-0"><ShieldCheck className="w-5 h-5 text-white" /></div>
            <div className="flex-1">
              <h3 className="text-white font-semibold mb-1">Enterprise Security</h3>
              <p className="text-zinc-600 text-xs leading-relaxed">SOC 2 Type II, SSO, SAML, end-to-end encryption. Your data never leaves your control.</p>
            </div>
            <div className="hidden sm:flex gap-2">
              {['SOC 2','SSO','E2EE','GDPR'].map(tag=>(<span key={tag} className="px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.06] text-[11px] text-zinc-400 font-medium">{tag}</span>))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Demo grid ─── */
function DemoGrid() {
  const [slots, setSlots] = useState(() => {
    const b = { '0-1': 'Team Standup', '1-3': 'Design Review', '2-0': 'Sprint Plan', '3-4': '1:1 Sarah', '4-2': 'Retro', '5-1': 'Demo Day', '6-3': 'Team Sync', '7-0': 'Wrap up', '8-4': 'Social' };
    const s = [];
    for (let ti = 0; ti < 9; ti++) {
      for (let d = 0; d < 8; d++) {
        const k = `${ti}-${d}`;
        s.push({ ti, d, booked: b[k] || null, we: d >= 6 });
      }
    }
    return s;
  });

  const toggle = (idx) => {
    setSlots(prev => prev.map((sl, i) => {
      if (i !== idx || sl.we) return sl;
      if (sl.booked) return { ...sl, booked: null };
      return { ...sl, booked: 'New Event' };
    }));
  };

  const times = ['9:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00'];
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

  return (
    <div id="demo" className="max-w-5xl mx-auto bg-zinc-900/60 backdrop-blur-xl border border-white/[0.06] rounded-3xl overflow-hidden" style={{ boxShadow: '0 40px 80px rgba(0,0,0,.4)' }}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.05]">
        <div className="flex items-center gap-4">
          <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-zinc-700" /><div className="w-3 h-3 rounded-full bg-zinc-700" /><div className="w-3 h-3 rounded-full bg-zinc-700" /></div>
          <div className="h-4 w-px bg-zinc-800" />
          <div className="flex items-center gap-2">
            <ChevronLeft className="w-[15px] h-[15px] text-zinc-600 cursor-pointer hover:text-white" />
            <span className="text-sm font-semibold text-white">Jun 16 – 22, 2026</span>
            <ChevronRight className="w-[15px] h-[15px] text-zinc-600 cursor-pointer hover:text-white" />
          </div>
        </div>
        <div className="flex items-center gap-1 bg-white/[0.03] rounded-xl p-1">
          <span className="text-xs px-3.5 py-1.5 text-zinc-600 rounded-lg cursor-pointer hover:text-zinc-400">Day</span>
          <span className="text-xs px-3.5 py-1.5 bg-white/[0.08] rounded-lg text-white font-medium">Week</span>
          <span className="text-xs px-3.5 py-1.5 text-zinc-600 rounded-lg cursor-pointer hover:text-zinc-400">Month</span>
        </div>
      </div>
      <div className="grid grid-cols-8 border-b border-white/[0.04]">
        <div className="p-3 text-[10px] text-zinc-700 text-center font-medium uppercase tracking-wider">GMT</div>
        {days.map((d,i) => (
          <div key={d} className={`p-3 text-center ${i===4?'bg-white/[0.02]':''}`}>
            <div className={`text-[10px] font-medium uppercase ${i>=5?'text-zinc-800':'text-zinc-600'}`}>{d}</div>
            <div className={`text-sm font-medium mt-0.5 ${i===4?'text-white font-bold':i>=5?'text-zinc-700':'text-zinc-400'}`}>{16+i}</div>
          </div>
        ))}
      </div>
      <div className="overflow-y-auto max-h-[340px]">
        {times.map((time,ti) => (
          <div key={ti} className="grid grid-cols-8">
            <div className="p-2 text-[10px] text-zinc-700 text-right pr-3 border-b border-white/[0.03] flex items-start justify-end" style={{minHeight:36}}>{time}</div>
            {slots.filter(s=>s.ti===ti).map((sl,i)=>{
              const idx = ti*8 + i;
              return (
                <div key={idx} onClick={()=>toggle(idx)}
                  className={`p-2 border-b border-r border-white/[0.03] cursor-pointer transition-all hover:bg-white/[0.06] hover:border-white/[0.12] min-h-[36px] ${sl.we?'opacity-20 pointer-events-none':''} ${sl.booked?'bg-white/[0.07] border-white/[0.1]':''}`}>
                  {sl.booked&&<div className="p-1.5 text-[10px] text-zinc-300 font-medium truncate leading-tight">{sl.booked}</div>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between px-6 py-3 border-t border-white/[0.04] bg-white/[0.01]">
        <span className="text-[11px] text-zinc-700">Click empty slots to book</span>
        <span className="text-[11px] text-zinc-700">4 meetings · 6.5 hours</span>
      </div>
    </div>
  );
}

/* ─── Pricing ─── */
function PricingSection() {
  const [yearly, setYearly] = useState(false);
  const prices = yearly ? ['$0','$10','$39'] : ['$0','$12','$49'];

  return (
    <section id="pricing" className="py-28 px-6 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-white/[0.02] rounded-full blur-[140px] pointer-events-none" />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-600 font-semibold mb-3">Pricing</p>
          <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4">Pay for what you use.<br />Nothing more.</h2>
        </div>
        <div className="flex items-center justify-center gap-4 mb-16">
          <span className={`text-sm font-medium ${!yearly?'text-white':'text-zinc-600'}`}>Monthly</span>
          <button onClick={()=>setYearly(!yearly)} className="relative w-14 h-7 rounded-full bg-zinc-800 border border-zinc-700 transition-all duration-300 cursor-pointer">
            <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-lg transition-transform duration-300 ${yearly?'translate-x-[28px]':'left-0.5'}`} />
          </button>
          <span className={`text-sm font-medium ${yearly?'text-white':'text-zinc-600'}`}>Yearly <span className="text-[11px] text-zinc-700 ml-1">-20%</span></span>
        </div>

        <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {[
            { name:'Free', desc:'For individuals', price:prices[0], period:'', features:['1 calendar','Unlimited bookings','Basic links','Email support'], cta:'Get started', pop:false },
            { name:'Professional', desc:'For growing teams', price:prices[1], period:'/mo', features:['5 calendars','Team scheduling','AI suggestions','All integrations','Analytics','Priority support'], cta:'Start free trial', pop:true },
            { name:'Enterprise', desc:'For orgs at scale', price:prices[2], period:'/mo', features:['Unlimited everything','SSO / SAML','Custom branding','Admin controls','SLA guarantee','Dedicated manager'], cta:'Contact sales', pop:false },
          ].map((plan)=>(
            <div key={plan.name} className={`p-8 rounded-[24px] transition-all duration-500 hover:-translate-y-[5px] ${plan.pop?'bg-white/[0.04] border border-white/[0.15] relative':'bg-white/[0.02] border border-white/[0.06]'}`}>
              {plan.pop&&<div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-5 py-1.5 bg-white text-black text-xs font-bold rounded-full tracking-wide">POPULAR</div>}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                <p className={`text-sm ${plan.pop?'text-zinc-500':'text-zinc-600'}`}>{plan.desc}</p>
              </div>
              <div className="mb-8">
                <span className="text-6xl font-extrabold text-white tracking-tight">{plan.price}</span>
                {plan.period&&<span className="text-sm text-zinc-500">{plan.period}</span>}
              </div>
              <Link to="/register" className={`block w-full py-3.5 rounded-xl text-sm font-medium text-center mb-8 transition-all ${plan.pop?'bg-white text-black hover:bg-zinc-200':'border border-white/[0.1] text-zinc-400 hover:bg-white/5 hover:border-white/25'}`}>{plan.cta}</Link>
              <ul className="space-y-3.5">
                {plan.features.map(f=>(<li key={f} className={`flex items-center gap-3 text-sm ${plan.pop?'text-zinc-300':'text-zinc-500'}`}><span className={plan.pop?'text-white':'text-zinc-600'}>&#10003;</span>{f}</li>))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CTA ─── */
function CTASection() {
  return (
    <section className="py-28 px-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.02]" style={{backgroundImage:'linear-gradient(rgba(255,255,255,.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.5) 1px,transparent 1px)',backgroundSize:'60px 60px'}} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-white/[0.03] rounded-full blur-[160px]" />
      <div className="relative z-10 max-w-4xl mx-auto text-center">
        <h2 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] mb-6">
          Stop wasting time<br /><span className="italic font-light text-zinc-500">scheduling</span> time.
        </h2>
        <p className="text-zinc-500 text-lg max-w-md mx-auto mb-10">Join 10,000+ teams who reclaimed their calendar. Free forever for individuals.</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/register" className="px-10 py-4 rounded-2xl text-base font-semibold bg-white text-black flex items-center gap-2.5 w-full sm:w-auto justify-center hover:-translate-y-0.5 transition-all duration-300">
            Start for free <ArrowRight className="w-[17px] h-[17px]" />
          </Link>
          <Link to="/login" className="px-10 py-4 rounded-2xl text-base font-medium border border-white/[0.1] text-zinc-400 w-full sm:w-auto text-center hover:bg-white/5 hover:border-white/25 transition-all">
            Log in
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─── Footer ─── */
function Footer() {
  return (
    <footer className="border-t border-white/[0.04] py-14 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 mb-12">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2.5 mb-4">
              <img src="https://img.icons8.com/parakeet-line/48/tear-off-calendar.png" alt="ScheduleX" className="w-7 h-7" />
              <span className="text-white font-bold tracking-tight">ScheduleX</span>
            </Link>
            <p className="text-zinc-700 text-sm leading-relaxed">Scheduling made simple for modern teams.</p>
          </div>
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.15em] text-zinc-600 font-semibold mb-4">Product</h4>
            <ul className="space-y-2.5">
              <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">Features</Link></li>
              <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">Pricing</Link></li>
              <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">Integrations</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.15em] text-zinc-600 font-semibold mb-4">Legal</h4>
            <ul className="space-y-2.5">
              <li><Link to="/privacy" className="text-sm text-zinc-600 hover:text-white transition-colors">Privacy</Link></li>
              <li><Link to="/terms" className="text-sm text-zinc-600 hover:text-white transition-colors">Terms</Link></li>
              <li><Link to="/security" className="text-sm text-zinc-600 hover:text-white transition-colors">Security</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.15em] text-zinc-600 font-semibold mb-4">Resources</h4>
            <ul className="space-y-2.5">
              <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">Help Center</Link></li>
              <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">API Docs</Link></li>
              <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">Status</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.15em] text-zinc-600 font-semibold mb-4">Company</h4>
            <ul className="space-y-2.5">
              <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">About</Link></li>
              <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/[0.04] pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-800">&copy; {new Date().getFullYear()} ScheduleX. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

/* ─── Main Page ─── */
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#09090b] text-[#fafafa] font-['Inter',sans-serif] overflow-x-hidden selection:bg-white/20">
      <Navbar />
      <HeroSection />
      <Marquee />
      <FeaturesSection />
      <section className="py-28 px-6 relative">
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[400px] bg-white/[0.015] rounded-full blur-[130px] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-600 font-semibold mb-3">Interactive Demo</p>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4">Click any slot. Go ahead.</h2>
            <p className="text-zinc-500 text-lg max-w-md mx-auto">This is exactly what your team sees. Try booking a time.</p>
          </div>
          <DemoGrid />
        </div>
      </section>
      <PricingSection />
      <CTASection />
      <Footer />

      <style>{`
        @keyframes marquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
        @keyframes scaleIn{from{opacity:0;transform:scale(0.9)}to{opacity:1;transform:scale(1)}}
        @keyframes float{0%,100%{transform:translateY(0) rotate(0deg)}50%{transform:translateY(-20px) rotate(1deg)}}
      `}</style>
    </div>
  );
}
